import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import {
  getToolOrDynamicToolName,
  isToolOrDynamicToolUIPart,
  type UIMessage,
} from 'ai';
import { ChatActionButtons } from './ChatActionButtons';
import { ChatImageBubble } from './ChatImageBubble';
import { useThemeColor } from '@/hooks/use-theme-color';
import { Spacing, BorderRadius } from '@/constants/spacing';
import type { VoiceActionItem } from '@/types/voice-transcription';
import type { ExtractFromImageOutput } from '@/types/image-extraction';

const VOICE_ACTIONS_TOOL = 'suggestVoiceActions';
const EXTRACT_FROM_IMAGE_TOOL = 'extractFromImage';

interface ChatMessageBubbleProps {
  message: UIMessage;
  accentColor: string;
  textColor: string;
  userName?: string;
  isLatestMessage?: boolean;
  onVoiceActionAddTask?: (item: VoiceActionItem) => void;
  onVoiceActionAddEvent?: (item: VoiceActionItem) => void;
  onReport?: (message: UIMessage) => void;
}

export function ChatMessageBubble({
  message,
  accentColor,
  textColor,
  userName = 'User',
  isLatestMessage = false,
  onVoiceActionAddTask,
  onVoiceActionAddEvent,
  onReport,
}: ChatMessageBubbleProps) {
  const isUser = message.role === 'user';
  const containerAlignment = isUser ? styles.containerUser : styles.containerAssistant;

  const primaryColor = useThemeColor({}, 'primary');
  const borderMuted = useThemeColor({}, 'borderMuted');
  const text = useThemeColor({}, 'text');
  const border = useThemeColor({}, 'border');
  const textMuted = useThemeColor({}, 'textMuted');

  // Extract text, image, voice-action, and extract-from-image parts
  const textParts = message.parts.filter(part => part.type === 'text');
  const imageParts = message.parts.filter(
    (part: any) =>
      part?.type === 'file' &&
      typeof part?.mediaType === 'string' &&
      part.mediaType.startsWith('image/'),
  ) as { type: 'file'; mediaType: string; url?: string; data?: string }[];

  const voiceActionParts: VoiceActionItem[][] = [];
  const extractImageParts: ExtractFromImageOutput[] = [];

  for (const part of message.parts) {
    if (!isToolOrDynamicToolUIPart(part)) continue;
    if (part.state !== 'output-available') continue;

    const toolName = getToolOrDynamicToolName(part);
    if (toolName === VOICE_ACTIONS_TOOL) {
      const output = part.output as { items?: VoiceActionItem[] } | undefined;
      if (output?.items?.length) voiceActionParts.push(output.items);
    } else if (toolName === EXTRACT_FROM_IMAGE_TOOL) {
      const output = part.output as ExtractFromImageOutput | undefined;
      if (output?.items?.length) extractImageParts.push(output);
    }
  }

  return (
    <View style={[styles.container, containerAlignment]}>
      {imageParts.map((part, index) => {
        const uri = (part.url as string | undefined) ?? (part.data as string | undefined) ?? null;
        return (
          <ChatImageBubble
            key={`${message.id}-image-${index}`}
            imageUri={uri}
            textColor={text}
          />
        );
      })}
      {/* Main text bubble — may include voice action buttons inside */}
      {(textParts.length > 0 || voiceActionParts.length > 0 || extractImageParts.length > 0) && (
        <View
          style={[
            styles.bubble,
            isUser
              ? [styles.userBubble, { backgroundColor: primaryColor }]
              : [styles.assistantBubble, { backgroundColor: borderMuted }],
          ]}>
          {textParts.map((part, index) => {
            if (part.type === 'text') {
              return (
                <Text
                  key={`${message.id}-text-${index}`}
                  style={[
                    styles.messageText,
                    { color: isUser ? '#FFFFFF' : text },
                  ]}>
                  {part.text}
                </Text>
              );
            }
            return null;
          })}

          {/* Voice action buttons inside the bubble */}
          {voiceActionParts.map((items, index) => (
            <ChatActionButtons
              key={`${message.id}-voice-actions-${index}`}
              items={items}
              accentColor={accentColor}
              onAddTask={(item) => onVoiceActionAddTask?.(item)}
              onAddEvent={(item) => onVoiceActionAddEvent?.(item)}
              onDismiss={() => {}}
              borderColor={border}
            />
          ))}

          {extractImageParts.map((output, index) => {
            const worst: 'high' | 'medium' | 'low' = output.items.reduce<'high' | 'medium' | 'low'>(
              (acc, item) => {
                if (item.confidence === 'low') return 'low';
                if (item.confidence === 'medium' && acc === 'high') return 'medium';
                return acc;
              },
              'high',
            );
            return (
              <ChatActionButtons
                key={`${message.id}-extract-image-${index}`}
                items={output.items}
                accentColor={accentColor}
                imageType={output.imageType}
                worstConfidence={worst}
                onAddTask={(item) => onVoiceActionAddTask?.(item)}
                onAddEvent={(item) => onVoiceActionAddEvent?.(item)}
                onDismiss={() => {}}
                borderColor={border}
              />
            );
          })}
        </View>
      )}

      {/* Report link — assistant text messages only */}
      {!isUser && onReport && textParts.length > 0 && (
        <TouchableOpacity
          onPress={() => onReport(message)}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          style={styles.reportRow}>
          <Ionicons name="flag-outline" size={12} color={textMuted} />
          <Text style={[styles.reportText, { color: textMuted }]}>Report</Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: Spacing.sm,
    marginBottom: Spacing.xs,
    width: '100%',
  },
  containerUser: {
    alignItems: 'flex-end',
  },
  containerAssistant: {
    alignItems: 'flex-start',
  },
  bubble: {
    maxWidth: '75%',
    borderRadius: BorderRadius.xl - 2,
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.sm,
  },
  userBubble: {
    borderBottomRightRadius: BorderRadius.xs,
  },
  assistantBubble: {
    borderBottomLeftRadius: BorderRadius.xs,
  },
  messageText: {
    fontSize: 14,
    lineHeight: 20,
  },
  reportRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: Spacing.xs,
  },
  reportText: {
    fontSize: 11,
    fontWeight: '500',
  },
});
